import { formatDistanceToNow } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  getConnector,
  getSourceDocuments,
  getSyncRuns,
  rotateCredentials,
  updateConnector,
} from '../../api/connectors'
import type { ConnectorResponse, SourceDocument, SyncRun } from '../../types/api'

interface ConnectorDetailProps {
  connectorId: number
  onClose: () => void
}

type Tab = 'documents' | 'history' | 'settings'

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-500/15 text-emerald-400',
  paused: 'bg-amber-500/15 text-amber-400',
  syncing: 'bg-blue-500/15 text-blue-400',
  degraded: 'bg-red-500/15 text-red-400',
  draft: 'bg-gray-500/15 text-gray-400',
}

const DOC_STATUS_STYLES: Record<string, string> = {
  indexed: 'text-emerald-400',
  processing: 'text-blue-400',
  pending: 'text-amber-400',
  failed: 'text-red-400',
}

const SOURCE_STATUS_STYLES: Record<string, string> = {
  active: 'text-emerald-400',
  deleted: 'text-red-400',
  out_of_scope: 'text-amber-400',
  inaccessible: 'text-harbor-text-muted',
}

export function ConnectorDetail({ connectorId, onClose }: ConnectorDetailProps) {
  const [connector, setConnector] = useState<ConnectorResponse | null>(null)
  const [tab, setTab] = useState<Tab>('documents')
  const [loading, setLoading] = useState(true)

  // Documents
  const [documents, setDocuments] = useState<SourceDocument[]>([])
  const [docsLoading, setDocsLoading] = useState(false)

  // History
  const [runs, setRuns] = useState<SyncRun[]>([])
  const [runsLoading, setRunsLoading] = useState(false)

  // Settings
  const [editName, setEditName] = useState('')
  const [newPat, setNewPat] = useState('')
  const [saving, setSaving] = useState(false)

  const loadConnector = useCallback(async () => {
    try {
      const data = await getConnector(connectorId)
      setConnector(data)
      setEditName(data.displayName)
    } catch {
      toast.error('Failed to load connector details')
    } finally {
      setLoading(false)
    }
  }, [connectorId])

  useEffect(() => {
    loadConnector()
  }, [loadConnector])

  const loadDocuments = useCallback(async () => {
    setDocsLoading(true)
    try {
      const data = await getSourceDocuments(connectorId)
      setDocuments(data)
    } catch {
      toast.error('Failed to load documents')
    } finally {
      setDocsLoading(false)
    }
  }, [connectorId])

  const loadRuns = useCallback(async () => {
    setRunsLoading(true)
    try {
      const data = await getSyncRuns(connectorId)
      setRuns(
        data.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
      )
    } catch {
      toast.error('Failed to load sync history')
    } finally {
      setRunsLoading(false)
    }
  }, [connectorId])

  useEffect(() => {
    if (tab === 'documents') loadDocuments()
    if (tab === 'history') loadRuns()
  }, [tab, loadDocuments, loadRuns])

  // Poll for updates when syncing or a run is still open
  const hasOpenRun = runs.some((r) => r.status === 'running')
  useEffect(() => {
    if (connector?.status !== 'syncing' && !hasOpenRun) return
    const interval = setInterval(() => {
      void loadConnector()
      if (tab === 'history') void loadRuns()
      if (tab === 'documents') void loadDocuments()
    }, 3000)
    return () => clearInterval(interval)
  }, [connector?.status, hasOpenRun, tab, loadConnector, loadRuns, loadDocuments])

  async function handleSaveName() {
    if (!editName.trim()) return
    setSaving(true)
    try {
      await updateConnector(connectorId, { displayName: editName })
      toast.success('Name updated')
      await loadConnector()
    } catch {
      toast.error('Failed to update name')
    } finally {
      setSaving(false)
    }
  }

  async function handleRotatePat() {
    if (!newPat.trim()) return
    setSaving(true)
    try {
      await rotateCredentials(connectorId, newPat)
      toast.success('Token rotated')
      setNewPat('')
    } catch {
      toast.error('Failed to rotate token')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-harbor-surface border border-harbor-border rounded-xl p-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500 mx-auto" />
        </div>
      </div>
    )
  }

  if (!connector) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-harbor-surface border border-harbor-border rounded-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-harbor-border">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-harbor-text">{connector.displayName}</h2>
              <span
                className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[connector.status] ?? STATUS_STYLES.draft}`}
              >
                {connector.status === 'syncing' && (
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse mr-1.5" />
                )}
                {connector.status.replace('_', ' ')}
              </span>
            </div>
            {connector.configJson &&
              (() => {
                try {
                  const cfg = JSON.parse(connector.configJson)
                  if (cfg.owner && cfg.repo) {
                    return (
                      <p className="text-sm text-harbor-text-secondary mt-0.5">
                        {cfg.owner}/{cfg.repo}
                        {cfg.branch && ` @ ${cfg.branch}`}
                        {cfg.pathPrefix && ` / ${cfg.pathPrefix}`}
                      </p>
                    )
                  }
                } catch {
                  return null
                }
                return null
              })()}
          </div>
          <button
            onClick={onClose}
            className="text-harbor-text-muted hover:text-harbor-text transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-harbor-border px-6">
          {(['documents', 'history', 'settings'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
              }`}
            >
              {t === 'documents' && 'Documents'}
              {t === 'history' && 'Sync History'}
              {t === 'settings' && 'Settings'}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Documents tab */}
          {tab === 'documents' && (
            <div>
              {docsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-500" />
                </div>
              ) : documents.length === 0 ? (
                <p className="text-sm text-harbor-text-secondary text-center py-8">
                  No documents synced yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-harbor-text-secondary border-b border-harbor-border">
                        <th className="pb-2 font-medium">Path</th>
                        <th className="pb-2 font-medium">Source</th>
                        <th className="pb-2 font-medium">Status</th>
                        <th className="pb-2 font-medium">Last Synced</th>
                        <th className="pb-2 font-medium">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-harbor-border/50">
                      {documents.map((doc) => (
                        <tr key={doc.id} className="text-harbor-text">
                          <td className="py-2 pr-4 font-mono text-xs truncate max-w-xs">
                            {doc.externalPath ?? doc.externalId}
                          </td>
                          <td className="py-2 pr-4">
                            <span
                              className={
                                SOURCE_STATUS_STYLES[doc.sourceStatus] ?? 'text-harbor-text-muted'
                              }
                            >
                              {doc.sourceStatus}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            <span
                              className={
                                DOC_STATUS_STYLES[doc.ingestionStatus] ?? 'text-harbor-text-muted'
                              }
                            >
                              {doc.ingestionStatus}
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-harbor-text-secondary text-xs">
                            {doc.lastSuccessAt
                              ? formatDistanceToNow(new Date(doc.lastSuccessAt), {
                                  addSuffix: true,
                                })
                              : doc.lastAttemptAt
                                ? formatDistanceToNow(new Date(doc.lastAttemptAt), {
                                    addSuffix: true,
                                  })
                                : '-'}
                          </td>
                          <td className="py-2 text-red-400 text-xs truncate max-w-xs">
                            {doc.lastError ?? '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* History tab */}
          {tab === 'history' && (
            <div>
              {runsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-500" />
                </div>
              ) : runs.length === 0 ? (
                <p className="text-sm text-harbor-text-secondary text-center py-8">
                  No sync runs yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {runs.map((run) => {
                    const duration = run.completedAt
                      ? Math.round(
                          (new Date(run.completedAt).getTime() -
                            new Date(run.startedAt).getTime()) /
                            1000
                        )
                      : null

                    return (
                      <div
                        key={run.id}
                        className="bg-harbor-surface-raised rounded-lg p-4 border border-harbor-border"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-2 h-2 rounded-full ${
                                run.status === 'completed'
                                  ? 'bg-emerald-400'
                                  : run.status === 'partial'
                                    ? 'bg-amber-400'
                                    : run.status === 'failed' || run.status === 'abandoned'
                                      ? 'bg-red-400'
                                      : run.status === 'running'
                                        ? 'bg-blue-400 animate-pulse'
                                        : 'bg-gray-400'
                              }`}
                            />
                            <span className="text-sm font-medium text-harbor-text capitalize">
                              {run.status === 'partial'
                                ? 'Partial'
                                : run.status === 'abandoned'
                                  ? 'Abandoned'
                                  : run.status}
                            </span>
                            <span className="text-xs text-harbor-text-muted">
                              {run.phase !== run.status &&
                                run.phase !== 'completed' &&
                                `(${run.phase})`}
                            </span>
                          </div>
                          <span className="text-xs text-harbor-text-muted">
                            {formatDistanceToNow(new Date(run.startedAt), { addSuffix: true })}
                            {duration != null && ` (${duration}s)`}
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <span className="text-harbor-text-muted">Events</span>
                            <p className="text-harbor-text">
                              {run.eventsInserted} inserted, {run.eventsProcessed} processed
                            </p>
                          </div>
                          <div>
                            <span className="text-harbor-text-muted">Failed</span>
                            <p
                              className={run.eventsFailed > 0 ? 'text-red-400' : 'text-harbor-text'}
                            >
                              {run.eventsFailed}
                            </p>
                          </div>
                          <div>
                            <span className="text-harbor-text-muted">Docs</span>
                            <p className="text-harbor-text">
                              {run.documentsUpserted ?? 0} upserted, {run.documentsDeleted ?? 0}{' '}
                              deleted
                            </p>
                          </div>
                        </div>

                        {run.errorMessage && (
                          <p className="text-xs text-red-400 mt-2 truncate">{run.errorMessage}</p>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Settings tab */}
          {tab === 'settings' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                  Display Name
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={saving || !editName.trim() || editName === connector.displayName}
                    className="px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                  Rotate Personal Access Token
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={newPat}
                    onChange={(e) => setNewPat(e.target.value)}
                    placeholder="New token"
                    className="flex-1 px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleRotatePat}
                    disabled={saving || !newPat.trim()}
                    className="px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    Rotate
                  </button>
                </div>
                {connector.credentialsUpdatedAt && (
                  <p className="text-xs text-harbor-text-muted mt-1">
                    Last updated{' '}
                    {formatDistanceToNow(new Date(connector.credentialsUpdatedAt), {
                      addSuffix: true,
                    })}
                  </p>
                )}
              </div>

              {connector.providerError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-sm text-red-400">{connector.providerError}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
